import React from 'react';
import { ArrowLeft, Cookie, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTrackLegalPageView } from '../hooks/useTrackLegalPageView';

const CookiePolicy = () => {
  const { isDarkMode } = useTheme();
  const currentDate = "August 2026";
  
  // Track page view
  useTrackLegalPageView('cookie-policy');
  
  const cookieTypes = [
    {
      name: "Essential Cookies",
      required: true,
      description: "These cookies are necessary for the website to function properly and cannot be disabled.",
      cookies: [
        { name: "neura_cookie_consent", purpose: "Stores your cookie preferences", duration: "1 year", provider: "NEURA AI" },
        { name: "neura_session", purpose: "Maintains your session while browsing", duration: "Session", provider: "NEURA AI" },
        { name: "neura_customer", purpose: "Keeps you logged in to your account", duration: "Session", provider: "NEURA AI" }
      ]
    },
    {
      name: "Analytics Cookies",
      required: false,
      description: "These cookies help us understand how visitors interact with our website by collecting anonymous information.",
      cookies: [
        { name: "_ga", purpose: "Distinguishes unique users", duration: "2 years", provider: "Google Analytics" },
        { name: "_ga_*", purpose: "Maintains session state", duration: "2 years", provider: "Google Analytics" },
        { name: "_gid", purpose: "Distinguishes users", duration: "24 hours", provider: "Google Analytics" }
      ]
    },
    {
      name: "Marketing Cookies",
      required: false,
      description: "These cookies are used to track visitors across websites to display relevant advertisements.",
      cookies: [
        { name: "_fbp", purpose: "Facebook advertising tracking", duration: "3 months", provider: "Facebook" },
        { name: "_gcl_au", purpose: "Google Ads conversion tracking", duration: "3 months", provider: "Google" }
      ]
    },
    {
      name: "Functional Cookies",
      required: false,
      description: "These cookies enable enhanced functionality and personalisation, such as remembering your preferences.",
      cookies: [
        { name: "neura_theme", purpose: "Remembers your dark/light mode preference", duration: "1 year", provider: "NEURA AI" },
        { name: "neura_chat_shown", purpose: "Tracks if welcome chat has been shown", duration: "Session", provider: "NEURA AI" }
      ]
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${isDarkMode ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'} backdrop-blur-sm`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <Link 
            to="/" 
            className={`inline-flex items-center gap-2 text-sm font-medium ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Title */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 ${isDarkMode ? 'bg-yellow-500/20' : 'bg-yellow-100'}`}>
            <Cookie className={`w-8 h-8 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
          </div>
          <h1 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Cookie Policy
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Last updated: {currentDate}
          </p>
        </div>

        {/* Policy Content */}
        <div className={`prose prose-lg max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
          
          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>What Are Cookies?</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.
            </p>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              This Cookie Policy explains how NEURA AI SOFTWARES ("we", "our", or "us") uses cookies and similar technologies on our website.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>How We Use Cookies</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We use cookies for the following purposes:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li><strong>Essential functionality:</strong> To enable core website features like navigation, secure access, and form submissions</li>
              <li><strong>Analytics:</strong> To understand how visitors use our website and identify areas for improvement</li>
              <li><strong>Marketing:</strong> To deliver relevant advertisements and measure campaign effectiveness</li>
              <li><strong>Personalisation:</strong> To remember your preferences and provide a customised experience</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Types of Cookies We Use</h2>
            
            {cookieTypes.map((type, index) => (
              <div key={index} className={`mb-8 p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {type.name}
                  </h3>
                  {type.required ? (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                      Always Active
                    </span>
                  ) : (
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                      Optional
                    </span>
                  )}
                </div>
                <p className={`mb-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {type.description}
                </p>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <th className={`text-left py-2 pr-4 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Cookie Name</th>
                        <th className={`text-left py-2 pr-4 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Purpose</th>
                        <th className={`text-left py-2 pr-4 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Duration</th>
                        <th className={`text-left py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Provider</th>
                      </tr>
                    </thead>
                    <tbody>
                      {type.cookies.map((cookie, cookieIndex) => (
                        <tr key={cookieIndex} className={`border-b last:border-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                          <td className={`py-2 pr-4 font-mono text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{cookie.name}</td>
                          <td className={`py-2 pr-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{cookie.purpose}</td>
                          <td className={`py-2 pr-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{cookie.duration}</td>
                          <td className={`py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{cookie.provider}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Third-Party Cookies</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Some cookies are placed by third-party services that appear on our pages. We use the following third-party services:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li><strong>Google Analytics:</strong> Web analytics service (<a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:underline">Privacy Policy</a>)</li>
              <li><strong>Google Maps:</strong> Embedded maps (<a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:underline">Privacy Policy</a>)</li>
              <li><strong>Stripe:</strong> Payment processing (<a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:underline">Privacy Policy</a>)</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Managing Your Cookie Preferences</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              When you first visit our website, you will see a cookie consent banner that allows you to:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li><strong>Accept All:</strong> Enable all cookies including analytics and marketing</li>
              <li><strong>Reject:</strong> Only essential cookies will be used</li>
              <li><strong>Manage:</strong> Choose which categories of cookies to enable</li>
            </ul>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              You can change your preferences at any time by clearing your browser's cookies and revisiting our site, or by contacting us directly.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Browser Cookie Settings</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Most web browsers allow you to control cookies through their settings. Here's how to manage cookies in popular browsers:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:underline">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:underline">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:underline">Safari</a></li>
              <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:underline">Microsoft Edge</a></li>
            </ul>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Please note that disabling certain cookies may affect the functionality of our website.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Changes to This Policy</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our business practices. We will post the updated policy on this page with a new "Last updated" date.
            </p>
          </section>

          <section className="mb-10">
            <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Contact Us</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              If you have any questions about our use of cookies, please contact us:
            </p>
            <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>NEURA AI SOFTWARES</p>
              <div className="space-y-3">
                <div className={`flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <MapPin className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                  <span>Belfast, Northern Ireland, United Kingdom</span>
                </div>
                <div className={`flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <Mail className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                  <a href="mailto:PeterG@neura-ai.info" className="hover:text-cyan-500 transition-colors">
                    PeterG@neura-ai.info
                  </a>
                </div>
              </div>
            </div>
          </section>

        </div>

        {/* Back to Home */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700 text-center">
          <Link 
            to="/" 
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-colors ${isDarkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className={`border-t py-6 ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            © {new Date().getFullYear()} NEURA AI SOFTWARES. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicy;
